"use client"

import { useContext, useState } from "react"
import { AppContext } from "../../contexts/AppContext"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  alpha,
  useTheme,
  InputAdornment,
  Paper,
  Fade,
  Stack,
  Chip,
  Tooltip,
} from "@mui/material"
import {
  AddCircle as AddIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
  CreditCard as CardIcon,
  Comment as CommentIcon,
  ShoppingCart as CartIcon,
  PointOfSale as PointsIcon,
  Info as InfoIcon,
  VerifiedUser,
  Person,
} from "@mui/icons-material"

const CardPointsAdd = ({ idCard, partnerId, name=null, onSave, onCancel }) => {
  const [amount, setAmount] = useState(undefined)
  const [purchaseValue, setPurchaseValue] = useState(undefined)
  const [comment, setComment] = useState("")
  const [addChoice, setAddChoice] = useState("Amount")
  const { state } = useContext(AppContext)
  const queryClient = useQueryClient()
  const theme = useTheme()

  const mutation = useMutation({
    mutationFn: async (payload) => {
      const url = `/api/v2/partner/${partnerId}/cards/${idCard}/points`

      const response = await axios.post(url, payload, {
        headers: {
          Authorization: state.token ? `Bearer ${state.token}` : "",
          "Content-Type": "application/json",
        },
      })

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["points"])
      onSave()
    },
    onError: (error) => {
      console.error("Error saving points:", error)
    },
  })

  const handleSubmit = () => {
    // Perform validation
    if (!amount && !purchaseValue) {
      alert("Please enter a valid amount or purchase value.")
      return
    }

    // Create the JSON payload
    const payload = {
      amount: addChoice === "Amount" ? amount : undefined,
      purchase_value: addChoice === "Purchase Value" ? purchaseValue : undefined,
      comment,
    }

    // Send the request
    console.log("Submitting payload:", payload)
    mutation.mutate(payload)

    // Reset the form
    setAmount(undefined)
    setPurchaseValue(undefined)
    setComment("")
  }

  return (
    <Fade in={true} timeout={400}>
      <Card
        elevation={3}
        sx={{
          borderRadius: 2,
          overflow: "visible",
          maxWidth: 500,
          mx: "auto",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <AddIcon color="primary" fontSize="large" />
              <Typography variant="h5" fontWeight="bold">
                Add Card Points
              </Typography>
            </Box>
          }
          subheader={
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                <CardIcon fontSize="small" sx={{ mr: 0.5, color: theme.palette.text.secondary }} />
                <Typography variant="body2" color="text.secondary">
                  Card ID: {idCard}
                </Typography>
              </Box>
              {name && <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                <Person fontSize="small" sx={{ mr: 0.5, color: theme.palette.text.secondary }} />
                <Typography variant="body2" color="text.secondary">
                  Name: {name}
                </Typography>
              </Box>}
            </Box>
          }
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderBottom: "1px solid",
            borderColor: "divider",
            pb: 2,
          }}
        />

        <CardContent sx={{ p: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              bgcolor: alpha(theme.palette.info.main, 0.05),
              border: "1px solid",
              borderColor: alpha(theme.palette.info.main, 0.2),
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
            }}
          >
            <InfoIcon color="info" sx={{ mr: 1.5 }} />
            <Typography variant="body2" color="text.secondary">
              {addChoice === "Amount"
                ? "Add points directly to the customer's loyalty card."
                : "Add points based on purchase value according to your loyalty program rules."}
            </Typography>
          </Paper>

          <Stack spacing={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="add-points-method-label">Add Points By</InputLabel>
              <Select
                labelId="add-points-method-label"
                value={addChoice}
                onChange={(e) => setAddChoice(e.target.value)}
                label="Add Points By"
                startAdornment={
                  <InputAdornment position="start" sx={{ ml: 1, mr: -0.5 }}>
                    {addChoice === "Amount" ? <PointsIcon color="primary" /> : <CartIcon color="secondary" />}
                  </InputAdornment>
                }
              >
                <MenuItem value="Amount">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PointsIcon sx={{ mr: 1 }} color="primary" />
                    Number of Points
                  </Box>
                </MenuItem>
                <MenuItem value="Purchase Value">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CartIcon sx={{ mr: 1 }} color="secondary" />
                    Purchase Value
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              label={addChoice === "Amount" ? "Number of Points" : "Purchase Value"}
              type="number"
              fullWidth
              autoFocus
              value={
                addChoice === "Amount"
                  ? amount === undefined
                    ? ""
                    : amount
                  : purchaseValue === undefined
                    ? ""
                    : purchaseValue
              }
              onChange={(e) =>
                addChoice === "Amount"
                  ? setAmount(e.target.value === "" ? undefined : Number(e.target.value))
                  : setPurchaseValue(e.target.value === "" ? undefined : Number(e.target.value))
              }
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {addChoice === "Amount" ? <PointsIcon color="action" /> : <CartIcon color="action" />}
                  </InputAdornment>
                ),
                endAdornment: addChoice === "Purchase Value" && (
                  <InputAdornment position="end">
                    <Chip
                      label="Currency"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 24, fontSize: "0.75rem" }}
                    />
                  </InputAdornment>
                ),
              }}
              placeholder={addChoice === "Amount" ? "Enter points to add" : "Enter purchase amount"}
              helperText={
                addChoice === "Amount"
                  ? "Enter the exact number of points to add"
                  : "Points will be calculated based on purchase value"
              }
            />

            <TextField
              label="Comment (Optional)"
              fullWidth
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              variant="outlined"
              multiline
              rows={2}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.5 }}>
                    <CommentIcon color="action" />
                  </InputAdornment>
                ),
              }}
              placeholder="Add a note about this transaction"
            />
          </Stack>
        </CardContent>

        <Divider />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: 3,
            bgcolor: alpha(theme.palette.background.default, 0.5),
          }}
        >
          <Tooltip title="Cancel and discard changes">
            <Button
              variant="outlined"
              color="error"
              onClick={onCancel}
              startIcon={<CancelIcon />}
              sx={{ borderRadius: 1.5 }}
            >
              Cancel
            </Button>
          </Tooltip>

          <Tooltip title={!amount && !purchaseValue ? "Please enter a value" : "Add points to card"}>
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={(!amount && !purchaseValue) || mutation.isPending}
                startIcon={mutation.isPending ? null : <SaveIcon />}
                sx={{
                  borderRadius: 1.5,
                  px: 3,
                  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    boxShadow: "0 6px 15px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                {mutation.isPending ? "Processing..." : "Add Points"}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Card>
    </Fade>
  )
}

export default CardPointsAdd

